import { Link } from 'react-router-dom'
import AuthorSocials from './AuthorSocials'
import MediaImage from './MediaImage'
import type { Author } from '../data/content'

function MailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="1" />
      <path d="M3 7l9 7 9-7" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5.93" />
      <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 7.07 7.07L14 18.07" />
    </svg>
  )
}

export default function AuthorBox({
  author,
  showPostsLink = true,
  reveal = true,
}: {
  author: Author
  showPostsLink?: boolean
  reveal?: boolean
}) {
  return (
    <aside
      data-reveal={reveal || undefined}
      className="mt-14 border border-rose-wash bg-sand px-6 py-6 sm:px-8 sm:py-7"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:gap-6">
        {author.photo ? (
          <MediaImage
            src={author.photo}
            size="thumb"
            alt={author.name}
            width={88}
            height={88}
            className="size-[88px] shrink-0 rounded-full object-cover object-top"
          />
        ) : (
          <span className="grid size-[88px] shrink-0 place-items-center rounded-full bg-rule font-serif text-2xl text-ink">
            {author.name.slice(0, 1)}
          </span>
        )}
        <div className="min-w-0">
          <h2 className="font-serif text-[22px] font-medium leading-tight">{author.name}</h2>
          <p className="mt-3 leading-relaxed text-copy">{author.bio}</p>
          {author.socials?.length ? <AuthorSocials socials={author.socials} /> : null}
          <div className="mt-5 flex flex-wrap items-center gap-2.5">
            {showPostsLink && (
              <Link
                to={`/author/${author.slug}/`}
                className="inline-flex h-9 items-center rounded-full bg-rose px-4 text-[12px] tracking-[0.04em] text-white hover:bg-ink"
              >
                View all posts
              </Link>
            )}
            {author.email && (
              <a
                href={`mailto:${author.email}`}
                aria-label={`Email ${author.name}`}
                className="grid size-9 place-items-center rounded-full bg-rose text-white hover:bg-ink"
              >
                <MailIcon />
              </a>
            )}
            {author.website && (
              <a
                href={author.website}
                aria-label={`${author.name}'s website`}
                target="_blank"
                rel="noreferrer"
                className="grid size-9 place-items-center rounded-full bg-rose text-white hover:bg-ink"
              >
                <LinkIcon />
              </a>
            )}
          </div>
        </div>
      </div>
    </aside>
  )
}
